import { Messages } from '@/constants/message';
import { useApi, useCaller } from '@/hooks/api.hook';
import {
  deleteHistoryChatAction,
  setListHistoryChatAction,
  setListMessageChatAction,
} from '@/redux/slicers/chatbot.slice';
import { generateUrlParams } from '@/utils/common';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

type FilterChatbotState = {
  Page: number;
  Size: number;
};

export const useChatbot = () => {
  const api = useApi('');
  const caller = useCaller();
  const dispatch = useDispatch();

  const getAllHistoryChatbot = useCallback(
    async ({ Page, Size }: FilterChatbotState) => {
      const queryParams: { [key: string]: string | undefined } = {
        Page: Page.toString(),
        Size: Size.toString(),
      };

      const urlParams = generateUrlParams(queryParams);

      const { content, succeeded, page, size, totalPages, totalElements } = await caller(
        () => api.get(`/v1/api/chatbot/sessions?${urlParams}`),
        {
          loadingKey: 'get-all-history-chatbot',
        },
      );

      if (succeeded) {
        dispatch(
          setListHistoryChatAction({
            data: content,
            pagination: {
              Page: page,
              totalPages,
              totalElements,
              Size: size,
            },
          }),
        );
      } else {
        dispatch(
          setListHistoryChatAction({
            data: [],
          }),
        );
      }
    },
    [caller, api],
  );

  const getAllMessageHistoryChatbot = useCallback(
    async ({ Page, Size }: FilterChatbotState, sessionId: string) => {
      const queryParams: { [key: string]: string | undefined } = {
        Page: Page.toString(),
        Size: Size.toString(),
      };

      const urlParams = generateUrlParams(queryParams);

      const { content, succeeded, page, size, totalPages, totalElements } = await caller(
        () => api.get(`/v1/api/chatbot/sessions/${sessionId}/messages?${urlParams}`),
        {
          loadingKey: 'get-all-history-chatbot',
        },
      );

      if (succeeded) {
        dispatch(
          setListMessageChatAction({
            data: content,
            pagination: {
              Page: page,
              totalPages,
              totalElements,
              Size: size,
            },
          }),
        );
      } else {
        dispatch(
          setListMessageChatAction({
            data: [],
          }),
        );
      }
    },
    [caller, api],
  );

  const deleteHistoryChat = useCallback(
    async (sessionId: string) => {
      const { succeeded } = await caller(() => api.del(`/v1/api/chatbot/sessions/${sessionId}`), {
        loadingKey: 'delete-history-chatbot',
        successMessage: Messages.DELETE_SUCCESS,
      });

      dispatch(deleteHistoryChatAction(sessionId));
      return !!succeeded;
    },

    [api, caller],
  );

  const createSessionId = useCallback(async () => {
    const response = await caller(
      () =>
        api.post('/v1/api/chatbot/sessions', {
          language: 'vi',
        }),
      {
        loadingKey: 'create-chatbot-session',
      },
    );

    // Backend returns: { sessionId, status, startedAt }
    // Note: Backend does NOT return 'succeeded' field
    const { sessionId, status, startedAt } = response;

    // Accept response if sessionId is present (backend doesn't return succeeded field)
    if (sessionId && typeof sessionId === 'string' && sessionId.length > 0) {
      // Store sessionId in localStorage

      return { sessionId, status, startedAt };
    }

    return null;
  }, [caller, api]);

  const sendMessage = useCallback(
    async (sessionId: string, content: string) => {
      const response = await caller(
        () =>
          api.post(`/v1/api/chatbot/sessions/${sessionId}/messages`, {
            content,
            metadata: {
              source: 'web',
            },
          }),
        {
          loadingKey: 'send-chatbot-message',
          returnErrorResponse: true, // Get response even if succeeded is undefined
        },
      );

      const { data } = response || {};

      // Backend returns: { messageId, response, intent, processingTime }
      // Note: Backend does NOT return 'succeeded' field
      // Accept response if data is present and has messageId or response
      if (data && (data.messageId || data.response)) {
        return data;
      }

      // If response itself has messageId/response (not nested in data)
      if (response && (response.messageId || response.response)) {
        return response;
      }

      return null;
    },
    [caller, api],
  );

  return {
    getAllHistoryChatbot,
    createSessionId,
    getAllMessageHistoryChatbot,
    deleteHistoryChat,
    sendMessage,
  };
};
